import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { FOUNDATION } from '../config/foundation';

export function Privacy() {
  usePageTitle('Privacy Policy');
  return (
    <Layout>
      <PageHero eyebrow="Legal" title={<>Privacy Policy</>}>
        Last updated March 2026
      </PageHero>
      <Reveal className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <div className="prose prose-sm max-w-none text-mustard-charcoal/80 space-y-6">
          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">1. Who we are</h2>
            <p>{FOUNDATION.name} ("we", "us", "our") is a {FOUNDATION.structure} registered in England and Wales (Charity No. {FOUNDATION.charityNumber}). We operate the LifeSpark360&#8482; platform and associated subdomains. We are the data controller for the personal data processed through this platform.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">2. What data we collect</h2>
            <p>We collect the following categories of personal data:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li><strong>Account information:</strong> name, email address, and password (stored as a cryptographic hash)</li>
              <li><strong>Profile data:</strong> phone number, location, profile type, and LinkedIn URL (if provided)</li>
              <li><strong>Assessment data:</strong> your responses to the LifeSpark360 Readiness Assessment and the resulting scores</li>
              <li><strong>CV data:</strong> uploaded CV files and the AI-generated review results</li>
              <li><strong>Programme data:</strong> session records, pre-session questionnaire responses, workbook submissions, and micro-action progress</li>
              <li><strong>Technical data:</strong> IP address (for rate limiting only, not logged), browser type, and request timestamps</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">3. How we use your data</h2>
            <p>We use your personal data for the following purposes:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li>To provide and operate the LifeSpark360 and Seedbed coaching programmes</li>
              <li>To score your readiness assessment and recommend the appropriate programme</li>
              <li>To analyse your CV using AI and provide structured feedback</li>
              <li>To enable your assigned trainer to deliver coaching sessions effectively</li>
              <li>To send transactional emails (session reminders, document releases, programme updates)</li>
              <li>To maintain platform security and prevent abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">4. Legal basis for processing</h2>
            <p>We process your data under the following legal bases:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li><strong>Contract:</strong> processing necessary to deliver the coaching programme you have enrolled in</li>
              <li><strong>Consent:</strong> for the readiness assessment and CV review (submitted voluntarily)</li>
              <li><strong>Legitimate interest:</strong> platform security, fraud prevention, and service improvement</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">5. Data sharing</h2>
            <p>We share your data only with:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li><strong>Your assigned trainer:</strong> session notes, PSQ responses, micro-action status, and assessment results</li>
              <li><strong>Cloudflare:</strong> our infrastructure provider (data processing agreement in place, EU data residency)</li>
              <li><strong>Resend:</strong> for transactional email delivery</li>
            </ul>
            <p className="mt-2">We do not sell your personal data. We do not share it with advertisers or marketing platforms.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">6. Data storage and security</h2>
            <p>All data is stored on Cloudflare infrastructure within the European Union (EU). Sensitive fields (email, phone, personal notes) are encrypted at the application layer using AES-256. All traffic is encrypted in transit using TLS 1.3. CV files are stored in private object storage accessible only via time-limited signed URLs.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">7. Data retention</h2>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li><strong>Account data:</strong> retained until you request deletion</li>
              <li><strong>Assessment data:</strong> retained for 2 years after completion</li>
              <li><strong>Programme data:</strong> retained for 2 years after programme completion</li>
              <li><strong>CV files:</strong> retained for 1 year after review, then automatically deleted</li>
              <li><strong>Audit logs:</strong> retained for 1 year for security purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">8. Your rights</h2>
            <p>Under UK GDPR, you have the right to:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li><strong>Access:</strong> request a copy of all personal data we hold about you</li>
              <li><strong>Rectification:</strong> correct any inaccurate personal data</li>
              <li><strong>Erasure:</strong> request deletion of your personal data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> receive your data in a machine-readable format</li>
              <li><strong>Objection:</strong> object to processing based on legitimate interest</li>
              <li><strong>Restriction:</strong> request restriction of processing while a complaint is resolved</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, contact us at <strong>{FOUNDATION.email}</strong>. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">9. Cookies</h2>
            <p>This platform uses only essential cookies required for authentication and security. We do not use tracking cookies, analytics cookies, or advertising cookies.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">10. Contact</h2>
            <p>For any questions about this privacy policy or your personal data, contact:</p>
            <p className="mt-2"><strong>{FOUNDATION.name}</strong><br />Email: {FOUNDATION.email}</p>
          </section>
        </div>
      </Reveal>
    </Layout>
  );
}
